import { compact, get, orderBy, range, reverse, sortBy } from "lodash";
import {
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  AssessmentGroupDelivery,
  AssessmentQuestionType,
  AssessmentType,
  ComplianceProgramCalibrationType,
  ComplianceProgramPerformanceReportRowFragment,
  ComplianceProgramReportPrevAnswerFragment,
  Maybe,
  OverallGoalAssessmentQuestionNode,
  OverallRecognitionCoreValueAssessmentQuestionNode,
  RangeAssessmentQuestionNode,
  TalentIndicatorAssessmentQuestionNode,
  TalentIndicatorType,
} from "types/graphql-schema";

import PerformanceAssessmentReportContext from "@apps/reporting/context/performance-assessment-report-context";
import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Callout from "@components/callout/callout";
import Table, {
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableContainer,
  TableHeadCell,
  TableHeadRow,
} from "@components/table/table";
import { TableSortDir, TableSticky } from "@components/table/table-constants";
import Tooltip from "@components/tooltip/tooltip";
import { classNames } from "@helpers/css";
import { assertNonNull } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";
import { pluralize } from "@helpers/string";

import { isAssessmentReportAnswerType } from "./helpers";
import { ChartLabel } from "./nine-box/types";
import PerformanceAssessmentTableRow from "./performance-assessment-table-row";
import AssessmentReportViewOptionsSidebar from "./report-filters/assessment-report-view-options-sidebar";
import {
  AssessmentReportSort,
  AssessmentReportSortKey,
  AssessmentReportSortType,
  AssessmentReportTableColumn,
  AssessmentReportViewOptions,
} from "./report-filters/types";

export type PerformanceAssessmentTableQuestion =
  | Pick<
      RangeAssessmentQuestionNode,
      | "__typename"
      | "id"
      | "title"
      | "startValue"
      | "endValue"
      | "labels"
      | "questionType"
      | "labelDescriptions"
      | "comment"
    >
  | Pick<
      TalentIndicatorAssessmentQuestionNode,
      | "__typename"
      | "id"
      | "title"
      | "startValue"
      | "endValue"
      | "labels"
      | "questionType"
      | "labelDescriptions"
      | "comment"
    >
  | Pick<
      OverallGoalAssessmentQuestionNode,
      | "__typename"
      | "id"
      | "title"
      | "questionType"
      | "startValue"
      | "endValue"
      | "labels"
      | "labelDescriptions"
      | "comment"
    >
  | Pick<
      OverallRecognitionCoreValueAssessmentQuestionNode,
      | "__typename"
      | "id"
      | "title"
      | "questionType"
      | "startValue"
      | "endValue"
      | "labels"
      | "labelDescriptions"
      | "comment"
    >;

const PerformanceAssessmentTableHeader = ({
  currentSortKey,
  currentSortDir,
  columnSortKey,
  handleColumnSort,
  children,
  labels,
  isFirst,
}: PropsWithChildren<{
  currentSortKey: AssessmentReportSort;
  columnSortKey: AssessmentReportSort;
  currentSortDir: TableSortDir;
  handleColumnSort: (sort: AssessmentReportSort) => void;
  labels?: ReadonlyArray<string | null>;
  isFirst?: boolean;
}>) => {
  return (
    <TableHeadCell
      cellClassName="max-w-[260px]"
      isFirst={isFirst}
      sorted={
        (currentSortKey.type === AssessmentReportSortType.KEY &&
          columnSortKey.type === AssessmentReportSortType.KEY &&
          currentSortKey.key === columnSortKey.key) ||
        (currentSortKey.type === AssessmentReportSortType.QUESTION_ID &&
          columnSortKey.type === AssessmentReportSortType.QUESTION_ID &&
          currentSortKey.questionId === columnSortKey.questionId) ||
        (currentSortKey.type === AssessmentReportSortType.ROLE_AVG &&
          columnSortKey.type === AssessmentReportSortType.ROLE_AVG) ||
        (currentSortKey.type === AssessmentReportSortType.SELF_ROLE_AVG &&
          columnSortKey.type === AssessmentReportSortType.SELF_ROLE_AVG)
          ? currentSortDir
          : undefined
      }
      onClick={() => handleColumnSort(columnSortKey)}
    >
      {!labels ? (
        children
      ) : (
        <Tooltip text={compact(labels).join(", ")}>
          <span>{children}</span>
        </Tooltip>
      )}
    </TableHeadCell>
  );
};

const PerformanceAssessmentTable = ({
  questions,
  filteredTableRows,
  talentIndicatorQuestions,
  showViewOptionsSidebar,
  calibrationGroupsEnabled,
  hiddenRowCount,
  recentAnswerQuestionIds,
  prevAnswersByAssessmentId,
  onUpdateViewOptions,
  onShowViewOptionsSidebar,
  onCloseViewOptionsSidebar,
  onUpdateChartLabels,
  onResetViewToBaseline,
  onSaveView,
  onSaveAsNewView,
  onDuplicateView,
  onDeleteView,
  onCreateCalibrationGroup,
}: {
  questions: PerformanceAssessmentTableQuestion[];
  filteredTableRows: ComplianceProgramPerformanceReportRowFragment[];
  talentIndicatorQuestions: (Pick<
    TalentIndicatorAssessmentQuestionNode,
    "id" | "startValue" | "endValue" | "labels"
  > & {
    talentIndicator?: Maybe<{
      id: number;
      name: string;
      customName?: Maybe<string>;
      talentIndicatorType: TalentIndicatorType;
    }>;
  })[];
  showViewOptionsSidebar: boolean;
  calibrationGroupsEnabled: boolean;
  hiddenRowCount: number;
  recentAnswerQuestionIds: number[];
  prevAnswersByAssessmentId: Record<
    number,
    ComplianceProgramReportPrevAnswerFragment[]
  >;
  onUpdateViewOptions: (opts: Partial<AssessmentReportViewOptions>) => void;
  onShowViewOptionsSidebar: () => void;
  onCloseViewOptionsSidebar: () => void;
  onUpdateChartLabels: (labels: ChartLabel[]) => void;
  onResetViewToBaseline: () => void;
  onSaveView: () => void;
  onSaveAsNewView: () => void;
  onDuplicateView: () => void;
  onDeleteView: () => void;
  onCreateCalibrationGroup: () => void;
}) => {
  const label = useLabel();
  const { complianceProgram, chartLabels, viewOptions, calibrationGroups } =
    useContext(PerformanceAssessmentReportContext);

  const [sortKey, setSortKey] = useState<AssessmentReportSort>(
    complianceProgram?.ongoing
      ? {
          type: AssessmentReportSortType.KEY,
          key: AssessmentReportSortKey.ENROLLMENT_DATE,
        }
      : {
          type: AssessmentReportSortType.KEY,
          key: AssessmentReportSortKey.NAME,
        },
  );
  const [sortDir, setSortDir] = useState<TableSortDir>(
    complianceProgram?.ongoing ? "desc" : "asc",
  );
  const assessmentTemplate = useMemo(
    () => assertNonNull(complianceProgram?.performanceAssessmentTemplate),
    [complianceProgram],
  );

  const hasRoleBasedQuestions = useMemo(() => {
    return assessmentTemplate.enableRoleBasedAssessments;
  }, [assessmentTemplate]);

  const showRoleColumn = useMemo(() => {
    return filteredTableRows.some((row) => {
      return row.roles.length > 0;
    });
  }, [filteredTableRows]);

  const handleColumnSort = useCallback(
    (newSort: AssessmentReportSort) => {
      if (
        newSort.type !== sortKey.type ||
        (newSort.type === AssessmentReportSortType.KEY &&
          sortKey.type === AssessmentReportSortType.KEY &&
          newSort.key !== sortKey.key) ||
        (newSort.type === AssessmentReportSortType.QUESTION_ID &&
          sortKey.type === AssessmentReportSortType.QUESTION_ID &&
          newSort.questionId !== sortKey.questionId)
      ) {
        setSortKey(newSort);
        setSortDir("asc");
      } else {
        setSortDir(sortDir === "asc" ? "desc" : "asc");
      }
    },
    [sortDir, sortKey],
  );

  const sortedUserRows = useMemo(() => {
    const sorted = orderBy(
      filteredTableRows.map((row) => ({
        ...row,
        calibrationGroupName: calibrationGroups.data.find((group) => {
          return group.users.map(({ id }) => id).includes(row.user.id);
        })?.name,
      })),
      (row) => {
        const assessment = row.assessment;

        if (sortKey.type === AssessmentReportSortType.CALIBRATION_GROUP) {
          return row.calibrationGroupName;
        }

        if (sortKey.type === AssessmentReportSortType.KEY) {
          if (sortKey.key === AssessmentReportSortKey.NAME) {
            return row.user.name;
          }
          if (sortKey.key === AssessmentReportSortKey.ENROLLMENT_DATE) {
            return row.targetAddedToProgramOn;
          }
          if (sortKey.key === AssessmentReportSortKey.ROLE) {
            const roles = row.roles;
            return roles.length > 0
              ? roles.map((role) => `${role.title}`).join(", ")
              : "";
          }
          if (sortKey.key === AssessmentReportSortKey.CAREER_TRACK) {
            const roles = row.roles;
            return roles.length > 0
              ? roles.map((role) => `${role.careerTrack?.title}`).join(", ")
              : "";
          }
          if (sortKey.key === AssessmentReportSortKey.CAREER_LEVEL) {
            const roles = row.roles;
            return roles.length > 0
              ? roles.map((role) => `${role.level}`).join(", ")
              : "";
          }
          if (sortKey.key === AssessmentReportSortKey.TEAM) {
            const teams = row.user.teamsList;
            return teams && teams.length > 0
              ? teams.map(({ title }) => title).join(", ")
              : "";
          }
          if (sortKey.key === AssessmentReportSortKey.POSITION) {
            return row.position ?? "";
          }
          if (sortKey.key === AssessmentReportSortKey.MANAGER) {
            return assessment
              ? assessment.responder?.name
              : row.user.primaryManager?.name;
          }
          if (sortKey.key === AssessmentReportSortKey.ASSESSMENT_STATUS) {
            return !!assessment;
          }
          if (sortKey.key === AssessmentReportSortKey.CALIBRATION_STATUS) {
            return row.calibrationStatus;
          }
        }

        if (assessment) {
          if (sortKey.type === AssessmentReportSortType.KEY) {
            if (sortKey.key === AssessmentReportSortKey.DELIVERY_STATUS) {
              return assessment.assessmentDelivery
                ? assessment.assessmentDelivery.state
                : assessment.assessmentDeliveryState;
            }
          }
          if (sortKey.type === AssessmentReportSortType.QUESTION_ID) {
            const question = assertNonNull(
              questions.find(({ id }) => id === sortKey.questionId),
            );
            const answer = assessment.answers.find(
              (answer) => answer?.questionId === question.id,
            );
            if (answer && isAssessmentReportAnswerType(answer)) {
              const integerAnswer = answer.integerAnswer;
              const qLabels = range(
                question.startValue,
                question.endValue + 1,
              ).reduce(
                (labelObj, value, index) => ({
                  ...labelObj,
                  [value]: question.labels[index] || value,
                }),
                {} as { [key: number]: string | number },
              );
              return integerAnswer ? qLabels[integerAnswer] : "";
            }
            return "";
          }
          if (sortKey.type === AssessmentReportSortType.ROLE_AVG) {
            return assessment.roleAssessmentAvg ?? "";
          }
          if (sortKey.type === AssessmentReportSortType.SELF_ROLE_AVG) {
            return assessment.selfRoleAssessmentAvg ?? "";
          }
        }
        return "";
      },
    );
    if (sortDir === "desc") {
      return reverse(sorted);
    }
    return sorted;
  }, [filteredTableRows, sortKey, sortDir, questions, calibrationGroups]);

  const showCalibration =
    complianceProgram?.calibration !== ComplianceProgramCalibrationType.Off;
  const showDeliveryStatus = [
    AssessmentGroupDelivery.Partial,
    AssessmentGroupDelivery.AdminApproval,
  ].includes(assessmentTemplate.delivery);

  const filteredQuestionForColumns = questions.filter(
    (question) => question.questionType !== AssessmentQuestionType.Text,
  );

  const tableColumns: AssessmentReportTableColumn[] = useMemo(() => {
    return compact([
      {
        id: "name",
        label: "Name",
        columnSortKey: {
          type: AssessmentReportSortType.KEY,
          key: AssessmentReportSortKey.NAME,
        } as AssessmentReportSort,
      },
      complianceProgram?.ongoing && {
        id: "enrollment-date",
        label: "Enrollment Date",
        columnSortKey: {
          type: AssessmentReportSortType.KEY,
          key: AssessmentReportSortKey.ENROLLMENT_DATE,
        } as AssessmentReportSort,
      },
      {
        id: "manager-status",
        label: `Downward ${label("review", { capitalize: true })}`,
        columnSortKey: {
          type: AssessmentReportSortType.KEY,
          key: AssessmentReportSortKey.ASSESSMENT_STATUS,
        } as AssessmentReportSort,
      },
      showCalibration && {
        id: "calibration-status",
        label: "Calibration",
        columnSortKey: {
          type: AssessmentReportSortType.KEY,
          key: AssessmentReportSortKey.CALIBRATION_STATUS,
        } as AssessmentReportSort,
      },
      showDeliveryStatus && {
        id: "status",
        label: "Share with Subject",
        columnSortKey: {
          type: AssessmentReportSortType.KEY,
          key: AssessmentReportSortKey.DELIVERY_STATUS,
        } as AssessmentReportSort,
      },
      calibrationGroups.showColumn && {
        id: "calibration-group",
        label: "Calibration Group",
        columnSortKey: {
          type: AssessmentReportSortType.CALIBRATION_GROUP,
        } as AssessmentReportSort,
      },
      showRoleColumn && {
        id: "current-role-track",
        label: "Career Track",
        columnSortKey: {
          type: AssessmentReportSortType.KEY,
          key: AssessmentReportSortKey.CAREER_TRACK,
        } as AssessmentReportSort,
      },
      showRoleColumn && {
        id: "current-role",
        label: "Current Role",
        columnSortKey: {
          type: AssessmentReportSortType.KEY,
          key: AssessmentReportSortKey.ROLE,
        } as AssessmentReportSort,
      },
      {
        id: "position",
        label: "Position",
        columnSortKey: {
          type: AssessmentReportSortType.KEY,
          key: AssessmentReportSortKey.POSITION,
        } as AssessmentReportSort,
      },
      showRoleColumn && {
        id: "current-role-level",
        label: "Level",
        columnSortKey: {
          type: AssessmentReportSortType.KEY,
          key: AssessmentReportSortKey.CAREER_LEVEL,
        } as AssessmentReportSort,
      },
      {
        id: "manager",
        label: "Manager",
        columnSortKey: {
          type: AssessmentReportSortType.KEY,
          key: AssessmentReportSortKey.MANAGER,
        } as AssessmentReportSort,
      },
      {
        // eslint-disable-next-line local-rules/use-label
        id: "team",
        label: label("team", { pluralize: true, capitalize: true }),
        columnSortKey: {
          type: AssessmentReportSortType.KEY,
          key: AssessmentReportSortKey.TEAM,
        } as AssessmentReportSort,
      },
      ...filteredQuestionForColumns.map((question) => ({
        id: question.id,
        label: question.title,
        columnSortKey: {
          type: AssessmentReportSortType.QUESTION_ID,
          questionId: question.id,
        } as AssessmentReportSort,
        questionLabels: [...question.labels],
      })),
      hasRoleBasedQuestions && {
        id: "role-avg",
        label: "Manager Role Avg.",
        columnSortKey: {
          type: AssessmentReportSortType.ROLE_AVG,
        } as AssessmentReportSort,
        questionLabels: [...assessmentTemplate.roleBasedAssessmentScaleLabels],
      },
      hasRoleBasedQuestions && {
        id: "self-role-avg",
        label: "Self Role Avg.",
        columnSortKey: {
          type: AssessmentReportSortType.SELF_ROLE_AVG,
        } as AssessmentReportSort,
        questionLabels: [...assessmentTemplate.roleBasedAssessmentScaleLabels],
      },
    ]);
  }, [
    assessmentTemplate,
    hasRoleBasedQuestions,
    label,
    filteredQuestionForColumns,
    showCalibration,
    showDeliveryStatus,
    showRoleColumn,
    complianceProgram,
    calibrationGroups.showColumn,
  ]);

  const sortedTableColumns = useMemo(() => {
    const defaultPositions = tableColumns.reduce(
      (acc, col, index) => {
        acc[col.id] = index;
        return acc;
      },
      {} as Record<string, number>,
    );
    const hiddenColumnIds = viewOptions.tableColumns
      .filter((col) => col.hidden)
      .map((col) => col.id);
    const visibleColumns = tableColumns.filter(
      (col) => !hiddenColumnIds.includes(col.id),
    );
    return sortBy(visibleColumns, (col) => {
      const colIndex = viewOptions.tableColumns.findIndex(
        ({ id }) => id === col.id,
      );
      return colIndex === -1 ? defaultPositions[col.id] : colIndex;
    });
  }, [tableColumns, viewOptions.tableColumns]);

  const {
    ConfirmationDialog: RowConfirmationDialog,
    confirm: rowActionConfirm,
    setMessage: setRowActionConfirmMessage,
  } = useConfirm("Are you sure?", "Do you want to continue?");
  const handleRowActionConfirm = useCallback(
    async (message: string) => {
      setRowActionConfirmMessage(message);
      return await rowActionConfirm();
    },
    [rowActionConfirm, setRowActionConfirmMessage],
  );

  const includedRecentQuestionIds = useMemo(() => {
    const columnIds = sortedTableColumns.map((col) => col.id);
    return recentAnswerQuestionIds.filter((id) => columnIds.includes(id));
  }, [sortedTableColumns, recentAnswerQuestionIds]);

  const visibleRecentAnswerColumns = useMemo(() => {
    return recentAnswerQuestionIds.filter(
      (questionId) =>
        !viewOptions.hiddenRecentAnswers ||
        !viewOptions.hiddenRecentAnswers.includes(questionId),
    );
  }, [recentAnswerQuestionIds, viewOptions.hiddenRecentAnswers]);

  if (assessmentTemplate.isOnlySelfAssessment) {
    return (
      <div className="mx-auto my-12 text-center text-gray-400">
        {`This performance ${label(
          "review",
        )} only contains self assessment questions. There is no data to display.`}
      </div>
    );
  }

  return (
    <>
      <RowConfirmationDialog />
      {showViewOptionsSidebar && (
        <AssessmentReportViewOptionsSidebar
          assessmentType={AssessmentType.Performance}
          calibrationGroupsEnabled={calibrationGroupsEnabled}
          isOngoingProgram={!!complianceProgram?.ongoing}
          viewOptions={viewOptions}
          talentIndicatorQuestions={talentIndicatorQuestions}
          recentAnswersQuestionIds={includedRecentQuestionIds}
          onUpdateViewOptions={onUpdateViewOptions}
          onClose={onCloseViewOptionsSidebar}
          tableColumns={tableColumns}
          questions={questions}
          chartLabels={chartLabels}
          onUpdateChartLabels={onUpdateChartLabels}
          onDeleteView={onDeleteView}
          onDuplicateView={onDuplicateView}
          onSaveView={onSaveView}
          onSaveAsNewView={onSaveAsNewView}
          onResetViewToBaseline={onResetViewToBaseline}
          onCreateCalibrationGroup={onCreateCalibrationGroup}
        />
      )}
      {filteredQuestionForColumns.length === 0 && !hasRoleBasedQuestions && (
        <Callout
          icon="warning"
          theme="warning"
          title="This program doesn't have any Range Questions"
          center
          className="mb-4"
        >
          Because there aren't any range questions used in this program, there
          are no responses to show.
        </Callout>
      )}
      <TableContainer
        scroll
        className={classNames(
          hiddenRowCount > 0 ? "max-h-[calc(100%-3rem)]" : "max-h-full",
        )}
      >
        <Table className="w-full" sticky={TableSticky.topAndLeft}>
          <TableHeadRow>
            {sortedTableColumns.map((column) => (
              <PerformanceAssessmentTableHeader
                key={column.id}
                currentSortKey={sortKey}
                currentSortDir={sortDir}
                handleColumnSort={handleColumnSort}
                columnSortKey={column.columnSortKey}
                labels={column.questionLabels}
              >
                {column.label}
              </PerformanceAssessmentTableHeader>
            ))}
          </TableHeadRow>
          <TableBody>
            {sortedUserRows.length === 0 && (
              <TableBodyRow>
                <TableBodyCell colSpan={sortedTableColumns.length}>
                  {sortedUserRows.length > 0 &&
                  viewOptions.filters.list.length > 0 ? (
                    <Callout
                      icon="info"
                      theme="transparent"
                      title="No employees matching the applied filters"
                      center
                    >
                      There are several employees in this table that are hidden
                      because of the filters you have applied.
                      <Button
                        text="Clear Filters"
                        onClick={() =>
                          onUpdateViewOptions({
                            filters: {
                              ...viewOptions.filters,
                              list: [],
                            },
                          })
                        }
                      />
                    </Callout>
                  ) : (
                    <Callout
                      icon="info"
                      theme="transparent"
                      title="No responses"
                      center
                    >
                      There haven't been any{" "}
                      {label("review", { pluralize: true })} delivered yet for
                      this program.
                      <Button
                        text="View Program Progress"
                        to={`/programs/${complianceProgram?.id}/progress`}
                      />
                    </Callout>
                  )}
                </TableBodyCell>
              </TableBodyRow>
            )}
            {sortedUserRows.map((reportRow) => {
              return (
                <PerformanceAssessmentTableRow
                  key={reportRow.id}
                  reportRow={reportRow}
                  questions={questions}
                  assessmentTemplate={assessmentTemplate}
                  visibleTableColumns={sortedTableColumns}
                  recentAnswerColumns={visibleRecentAnswerColumns}
                  prevAnswers={
                    reportRow.assessment?.id
                      ? get(
                          prevAnswersByAssessmentId,
                          reportRow.assessment.id,
                          [],
                        )
                      : []
                  }
                  onRowActionConfirm={handleRowActionConfirm}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {hiddenRowCount > 0 && (
        <div className="pt-6 flex justify-center">
          <div className="font-bold mr-1 mt-0.5">{`${hiddenRowCount} ${pluralize("people", hiddenRowCount)}`}</div>
          <div className="text-gray-600 mr-3 mt-0.5">hidden by filters</div>
          <Button
            text="View Options"
            theme={buttonTheme.default}
            onClick={onShowViewOptionsSidebar}
            size={ButtonSize.medium}
          />
        </div>
      )}
    </>
  );
};

export default PerformanceAssessmentTable;
